import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, X } from 'lucide-react';
import {
  INTERVENTION_PRIORITES,
  INTERVENTION_TYPES_DEMANDE,
  createDemandeInterventionSchema,
  type CreateDemandeInterventionInput,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { typeDemandeLabel } from './badges';

/**
 * Modal de création/édition d'une demande d'intervention (US-INT-02).
 *
 * En création : seuls les champs métadonnées sont posés (titre, description,
 * type, priorité, service demandeur, équipement, date planifiée souhaitée,
 * durée estimée). Le statut/technicien/etc. sont gérés par le workflow
 * (drawer 360°).
 */

const PRIORITE_LABELS: Record<(typeof INTERVENTION_PRIORITES)[number], string> = {
  P0: 'P0 — Critique',
  P1: 'P1 — Urgent',
  P2: 'P2 — Élevé',
  P3: 'P3 — Normal',
  P4: 'P4 — Standard',
  P5: 'P5 — Basse',
  P6: 'P6 — Basse',
  P7: 'P7 — Basse',
};

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}

interface EquipementOption {
  id: string;
  code_equipement: string;
  designation: string;
}

export function InterventionFormModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId?: string;
  onClose(): void;
  onSaved(id?: string): void;
}) {
  const isEdit = !!interventionId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [equipements, setEquipements] = useState<EquipementOption[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateDemandeInterventionInput>({
    resolver: zodResolver(
      createDemandeInterventionSchema,
    ) as unknown as Resolver<CreateDemandeInterventionInput>,
    defaultValues: {
      titre: '',
      description: '',
      type_demande: 'panne',
      priorite: 'P4',
      service_demandeur_id: null,
      equipement_id: null,
      date_planifiee: null,
      duree_estimee: null,
    },
  });

  useEffect(() => {
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
    api
      .get<{ data: EquipementOption[] }>('/equipements?page_size=200')
      .then((res) => setEquipements(res.data.data))
      .catch(() => setEquipements([]));
  }, []);

  useEffect(() => {
    if (!interventionId) return;
    (async () => {
      try {
        const res = await api.get<{
          data: {
            titre: string;
            description: string;
            type_demande: CreateDemandeInterventionInput['type_demande'];
            priorite: CreateDemandeInterventionInput['priorite'];
            service_demandeur_id: string | null;
            equipement_id: string | null;
            date_planifiee: string | null;
            duree_estimee: number | null;
          };
        }>(`/interventions/${interventionId}`);
        const d = res.data.data;
        reset({
          titre: d.titre,
          description: d.description,
          type_demande: d.type_demande,
          priorite: d.priorite,
          service_demandeur_id: d.service_demandeur_id,
          equipement_id: d.equipement_id,
          date_planifiee: d.date_planifiee,
          duree_estimee: d.duree_estimee,
        });
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [interventionId, reset]);

  async function onSubmit(values: CreateDemandeInterventionInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        service_demandeur_id: emptyToNull(values.service_demandeur_id),
        equipement_id: emptyToNull(values.equipement_id),
        date_planifiee: values.date_planifiee ? toIsoOrNull(values.date_planifiee) : null,
        duree_estimee:
          values.duree_estimee !== null && values.duree_estimee !== undefined
            ? Number(values.duree_estimee)
            : null,
      };
      if (isEdit) {
        const res = await api.patch<{ data: { id: string } }>(
          `/interventions/${interventionId}`,
          payload,
        );
        onSaved(res.data.data.id);
      } else {
        const res = await api.post<{ data: { id: string } }>('/interventions', payload);
        onSaved(res.data.data.id);
      }
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Modifier la demande' : "Nouvelle demande d'intervention"}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="intervention-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <Section title="Demande">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Titre *" error={errors.titre?.message} className="md:col-span-2">
                      <input
                        {...register('titre')}
                        className={inputClass}
                        placeholder="Ex. : Climatisation HS bloc B - étage 2"
                      />
                    </Field>
                    <Field
                      label="Description *"
                      error={errors.description?.message}
                      className="md:col-span-2"
                    >
                      <textarea
                        {...register('description')}
                        rows={4}
                        className={inputClass}
                        placeholder="Décrire le problème, les symptômes, le contexte…"
                      />
                    </Field>
                  </div>
                </Section>

                <Section title="Classification">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Type de demande *" error={errors.type_demande?.message}>
                      <select {...register('type_demande')} className={inputClass}>
                        {INTERVENTION_TYPES_DEMANDE.map((t) => (
                          <option key={t} value={t}>
                            {typeDemandeLabel(t)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Priorité *" error={errors.priorite?.message}>
                      <select {...register('priorite')} className={inputClass}>
                        {INTERVENTION_PRIORITES.map((p) => (
                          <option key={p} value={p}>
                            {PRIORITE_LABELS[p]}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </Section>

                <Section title="Localisation & équipement">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Service demandeur" error={errors.service_demandeur_id?.message}>
                      <select
                        {...register('service_demandeur_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                        })}
                        className={inputClass}
                      >
                        <option value="">— Aucun —</option>
                        {services.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.code} — {s.nom_complet}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Équipement" error={errors.equipement_id?.message}>
                      <select
                        {...register('equipement_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                        })}
                        className={inputClass}
                      >
                        <option value="">— Aucun —</option>
                        {equipements.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.code_equipement} — {e.designation}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </Section>

                <Section title="Planning (optionnel)">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Date souhaitée" error={errors.date_planifiee?.message}>
                      <input
                        type="datetime-local"
                        {...register('date_planifiee', {
                          setValueAs: (v) => (v === '' || v === null ? null : v),
                        })}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Durée estimée (minutes)" error={errors.duree_estimee?.message}>
                      <input
                        type="number"
                        min={1}
                        {...register('duree_estimee', {
                          setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                        })}
                        className={inputClass}
                      />
                    </Field>
                  </div>
                </Section>
              </form>
            )}
          </div>

          <div className="flex items-center justify-end p-4 border-t border-gray-200 flex-shrink-0 space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              form="intervention-form"
              disabled={saving || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Enregistrer' : 'Créer la demande'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 p-3">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function emptyToNull<T>(v: T | null | undefined): T | null {
  if (v === undefined || v === null || v === '') return null;
  return v;
}

function toIsoOrNull(v: string | null | undefined): string | null {
  if (!v) return null;
  // <input type="datetime-local"> renvoie "YYYY-MM-DDTHH:mm" sans timezone.
  // On le convertit en ISO complet UTC.
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch {
    return null;
  }
}
